import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";
import type { LogLevel, LogCategory } from "@/types/logger";

interface LogEntry {
  level: LogLevel;
  category: LogCategory;
  action: string;
  message: string;
  metadata?: Record<string, any>;
  duration_ms?: number;
  building_id?: string;
  error_stack?: string;
}

class Logger {
  private static instance: Logger;
  private currentUser: User | null = null;
  private isDevelopment = process.env.NODE_ENV === "development";

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  setUser(user: User | null) {
    this.currentUser = user;
  }

  /** Local developer diagnostics; callers decide when they are enabled. */
  warnLocal(message: string): void {
    console.warn(message);
  }

  private async persistLog(entry: LogEntry): Promise<void> {
    try {
      const logData = {
        level: entry.level,
        category: entry.category,
        action: entry.action,
        message: entry.message,
        metadata: entry.metadata,
        duration_ms: entry.duration_ms,
        building_id: entry.building_id ? parseInt(entry.building_id, 10) : undefined,
        user_id: this.currentUser?.id ? parseInt(this.currentUser.id) : undefined,
        error_stack: entry.error_stack,
        timestamp: new Date().toISOString(),
      };

      // Insert a single log entry
      const { error } = await supabase.from("system_logs").insert(logData);

      if (error) {
        console.error("Failed to persist log:", error);
      }
    } catch (error) {
      console.error("Error persisting log:", error);
    }
  }

  private formatMessage(entry: LogEntry): string {
    return `[${entry.category}] ${entry.action}: ${entry.message}`;
  }

  async log(entry: LogEntry): Promise<void> {
    // Always log to console in development
    if (this.isDevelopment) {
      const formattedMessage = this.formatMessage(entry);
      switch (entry.level) {
        case "debug":
          console.debug(formattedMessage, entry.metadata);
          break;
        case "info":
          console.info(formattedMessage, entry.metadata);
          break;
        case "warn":
          console.warn(formattedMessage, entry.metadata);
          break;
        case "error":
          console.error(formattedMessage, entry.metadata, entry.error_stack);
          break;
      }
    }

    // Persist to Supabase
    await this.persistLog(entry);
  }

  // Convenience methods for different log levels
  async debug(
    category: LogCategory,
    action: string,
    message: string,
    metadata?: Record<string, any>
  ) {
    await this.log({ level: "debug", category, action, message, metadata });
  }

  async info(
    category: LogCategory,
    action: string,
    message: string,
    metadata?: Record<string, any>
  ) {
    await this.log({ level: "info", category, action, message, metadata });
  }

  async warn(
    category: LogCategory,
    action: string,
    message: string,
    metadata?: Record<string, any>
  ) {
    await this.log({ level: "warn", category, action, message, metadata });
  }

  async error(
    category: LogCategory,
    action: string,
    message: string,
    error?: Error,
    metadata?: Record<string, any>
  ) {
    await this.log({
      level: "error",
      category,
      action,
      message,
      metadata,
      error_stack: error?.stack,
    });
  }

  // Performance monitoring helper
  async measurePerformance<T>(
    category: LogCategory,
    action: string,
    operation: () => Promise<T>
  ): Promise<T> {
    const startTime = performance.now();
    try {
      const result = await operation();
      const duration = Math.round(performance.now() - startTime);

      await this.info(category, action, `Completed in ${duration}ms`, {
        duration_ms: duration,
      });

      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);

      await this.error(category, action, `Failed after ${duration}ms`, error as Error, {
        duration_ms: duration,
      });

      throw error;
    }
  }
}

// Export a singleton instance
export const logger = Logger.getInstance();
